import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { anchorEl, ensureProjectsView } from './tasks-help.shared';

/**
 * ?-Hilfe der Auftragsuebersicht (Dialog, `project-overview-dialog.component.ts`, 08.10.2026).
 *
 * Die Flaeche der Leitung fuer EINEN Auftrag: Lage auf einen Blick, Kette, Eingriffe
 * (Schritt einfuegen, Nacharbeit, Termin, Abschluss) und alles, was am Auftrag passiert ist.
 *
 * Traegt drei Kernaussagen:
 *  - **Gearbeitet wird am Schritt.** Ein Klick auf einen Schritt oeffnet sein Detail; dort
 *    weist man neu zu und kommentiert. Die Uebersicht selbst hat kein Kommentarfeld (ENT5).
 *  - **Die dritte Kachel sagt, was gerade zaehlt** (Abschluss moeglich · Ausser Haus · Termin
 *    gefaehrdet · Als Naechstes — Vorrang in `project-overview.model.ts`).
 *  - **Eingreifen kann nur die Leitung** (⋯ und „Schritt einfuegen“ haengen an `menuVisible`);
 *    die Stationen dazu springen fuer alle anderen.
 *
 * Bis 1024 px stehen „Ablauf“ und „Aktivitaet“ in zwei Reitern; der jeweils andere ist per
 * `[hidden]` versteckt, sein Anker bleibt also im DOM. Die Stationen dieser Reiter schalten per
 * `showOverviewTab` selbst um und pruefen in `skipIf` nur Existenz (skipIf laeuft VOR
 * onBeforeShow — mit `notShown` fielen sie beim Zurueckblaettern weg). `notShown` bleibt fuer
 * Kopf, ⋯ und Fuss.
 */
const dialogStep = {
  attachToDialog: true,
  autoCloseOnDialogClose: true,
  popoverOutsideDialog: true,
} as const;

/**
 * Bis 1024 px stehen „Ablauf“ (0) und „Aktivität“ (1) in zwei Reitern. Vor einer Station
 * schaltet die Tour auf den Reiter ihres Ankers um — bis 09.10.2026 fiel „Aktivität“ am
 * Telefon einfach weg. Ohne Reiter (breiter Dialog) tut sie nichts.
 */
export async function showOverviewTab(index: 0 | 1): Promise<void> {
  const tab = anchorEl(AnchorId.TaskProjectOverviewDialog)
    ?.querySelectorAll<HTMLElement>('.po__tabs [role="tab"]')
    ?.[index];
  if (!tab || tab.getAttribute('aria-selected') === 'true') return;
  tab.click();
  await new Promise((resolve) => setTimeout(resolve, 260));
}

/** Anker fehlt ODER steht in einem versteckten Reiter (`[hidden]`). */
export function notShown(anchor: AnchorId): boolean {
  if (typeof document === 'undefined') return true;
  const el = anchorEl(anchor);
  return !el || !!el.closest('[hidden]');
}

export const TASK_PROJECT_OVERVIEW_HELP_TOUR: TourConfig = {
  id: TourFeature.TaskProjectOverviewHelp,
  version: 1,
  title: 'TASK_PROJECT_OVERVIEW_HELP_TOUR_TITLE',
  description: 'TASK_PROJECT_OVERVIEW_HELP_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  // Start aus dem Hilfe-Hub: Ansicht „Aufträge“, Titel der ersten Zeile. Aus dem offenen
  // Dialog heraus startet das ? direkt (ein Dialog ist schon offen → kein Oeffnen).
  launchContext: {
    // Ohne Query: die Seite übernimmt ?view= nur beim ersten Aufbau. `prepare` schaltet auf
    // „Aufträge“ (nicht bei offenem Dialog, also nie beim Start über das „?“ darin).
    route: '/tasks/overview',
    prepare: () => ensureProjectsView(),
    opens: { openerAnchor: AnchorId.TasksProjectRowOpen, settleMs: 700 },
    mode: 'auto',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 100,
  estimatedDurationSec: 170,
  category: 'feature-discovery',
  renumberVisibleSteps: true,
  chapters: [
    {
      id: 'overview',
      title: 'TASK_PROJECT_OVERVIEW_HELP_TOUR_TITLE',
      steps: [
        {
          id: 'po-welcome',
          anchor: AnchorId.TaskProjectOverviewDialog,
          spotlightAnchor: AnchorId.TaskProjectOverviewDialog,
          title: 'TUTORIAL.TASK_PROJECT_OVERVIEW.WELCOME.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_OVERVIEW.WELCOME.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: {
            ...dialogStep,
            waitForOpen: true,
            outsideDialog: true,
            highlightDialogHeader: true,
          },
          nextCondition: 'manual',
          showNextButton: true,
          showPrevButton: false,
        },
        {
          id: 'po-head',
          anchor: AnchorId.TaskProjectOverviewHead,
          spotlightAnchor: AnchorId.TaskProjectOverviewHead,
          title: 'TUTORIAL.TASK_PROJECT_OVERVIEW.HEAD.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_OVERVIEW.HEAD.CONTENT',
          placement: 'bottom',
          panelIndex: -1,
          dialogContext: { ...dialogStep, popoverOutsideDialog: false },
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => notShown(AnchorId.TaskProjectOverviewHead),
        },
        {
          id: 'po-situation',
          anchor: AnchorId.TaskProjectOverviewSituation,
          spotlightAnchor: AnchorId.TaskProjectOverviewSituation,
          title: 'TUTORIAL.TASK_PROJECT_OVERVIEW.SITUATION.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_OVERVIEW.SITUATION.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          onBeforeShow: () => showOverviewTab(0),
          skipIf: () => anchorEl(AnchorId.TaskProjectOverviewSituation) === null,
        },
        {
          id: 'po-chain',
          anchor: AnchorId.TaskProjectOverviewChain,
          spotlightAnchor: AnchorId.TaskProjectOverviewChain,
          title: 'TUTORIAL.TASK_PROJECT_OVERVIEW.CHAIN.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_OVERVIEW.CHAIN.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          onBeforeShow: () => showOverviewTab(0),
          skipIf: () => anchorEl(AnchorId.TaskProjectOverviewChain) === null,
        },
        {
          id: 'po-step',
          anchor: AnchorId.TaskProjectOverviewStep,
          spotlightAnchor: AnchorId.TaskProjectOverviewStep,
          title: 'TUTORIAL.TASK_PROJECT_OVERVIEW.STEP.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_OVERVIEW.STEP.CONTENT',
          placement: 'right',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          onBeforeShow: () => showOverviewTab(0),
          skipIf: () => anchorEl(AnchorId.TaskProjectOverviewStep) === null,
        },
        {
          // Nur Leitung, offener Auftrag (menuVisible).
          id: 'po-insert',
          anchor: AnchorId.TaskProjectOverviewAddStep,
          spotlightAnchor: AnchorId.TaskProjectOverviewAddStep,
          title: 'TUTORIAL.TASK_PROJECT_OVERVIEW.INSERT.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_OVERVIEW.INSERT.CONTENT',
          placement: 'left',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          onBeforeShow: () => showOverviewTab(0),
          skipIf: () => anchorEl(AnchorId.TaskProjectOverviewAddStep) === null,
        },
        {
          id: 'po-menu',
          anchor: AnchorId.TaskProjectOverviewMenu,
          spotlightAnchor: AnchorId.TaskProjectOverviewMenu,
          title: 'TUTORIAL.TASK_PROJECT_OVERVIEW.MENU.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_OVERVIEW.MENU.CONTENT',
          placement: 'bottom',
          align: 'end',
          panelIndex: -1,
          dialogContext: { ...dialogStep, popoverOutsideDialog: false },
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => notShown(AnchorId.TaskProjectOverviewMenu),
        },
        {
          id: 'po-activity',
          anchor: AnchorId.TaskProjectOverviewActivity,
          spotlightAnchor: AnchorId.TaskProjectOverviewActivity,
          title: 'TUTORIAL.TASK_PROJECT_OVERVIEW.ACTIVITY.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_OVERVIEW.ACTIVITY.CONTENT',
          // -v2 (11.10.2026): Kommentare am Auftrag sind schreibbar (R1.2).
          audioStepId: 'po-activity-v2',
          placement: 'left',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          // Am Telefon im zweiten Reiter: dorthin umschalten statt die Station auszulassen.
          onBeforeShow: () => showOverviewTab(1),
          skipIf: () => anchorEl(AnchorId.TaskProjectOverviewActivity) === null,
        },
        {
          // Nur mit „Abschluss vorschlagen“, wenn alle Schritte erledigt sind.
          id: 'po-close',
          anchor: AnchorId.TaskProjectOverviewClose,
          spotlightAnchor: AnchorId.TaskProjectOverviewClose,
          title: 'TUTORIAL.TASK_PROJECT_OVERVIEW.CLOSE.TITLE',
          content: 'TUTORIAL.TASK_PROJECT_OVERVIEW.CLOSE.CONTENT',
          placement: 'top',
          align: 'end',
          panelIndex: -1,
          dialogContext: dialogStep,
          nextCondition: 'manual',
          showNextButton: true,
          optional: true,
          skipIf: () => notShown(AnchorId.TaskProjectOverviewClose),
        },
      ],
    },
  ],
};
